import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, Loader2, Mail, Lock, User as UserIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GuidelinesContent } from "@/components/GuidelinesContent";

export default function Login() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get("invite");
  const [mode, setMode] = useState(inviteCode ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [inviter, setInviter] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [showRules, setShowRules] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (!inviteCode) return;
    api.get(`/invites/${inviteCode}`).then(({ data }) => setInviter(data.inviter)).catch(() => {});
  }, [inviteCode]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else {
        if (!agreed) { toast.error("Please accept the Community Guidelines"); return; }
        await register(form.name, form.email, form.password, inviteCode, agreed);
      }
      toast.success("Welcome to Xealth!");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row bg-background">
      {/* Brand panel */}
      <div className="relative lg:w-1/2 flex flex-col justify-center px-8 py-14 lg:px-16 overflow-hidden x-grid-bg">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#6C0BA9] opacity-30 blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-[#E024A3] opacity-20 blur-[120px]" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative z-10 max-w-md">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#6C0BA9] to-[#C084FC] flex items-center justify-center shadow-lg shadow-[#6C0BA9]/40">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading text-3xl font-extrabold tracking-tight">xealth</span>
          </div>
          <h1 className="font-heading text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight">
            Connect. Share.
            <span className="block bg-gradient-to-r from-[#C084FC] to-[#E024A3] bg-clip-text text-transparent">Be seen.</span>
          </h1>
          <p className="mt-5 text-base text-muted-foreground leading-relaxed">
            The bold new social network. Post moments, chat in real-time, and stand out with a glowing verified badge.
          </p>
        </motion.div>
      </div>

      {/* Form panel */}
      <div className="lg:w-1/2 flex items-center justify-center px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="w-full max-w-sm">
          <div className="bg-card border border-border rounded-3xl p-7 shadow-2xl">
            {inviter && (
              <div data-testid="invite-banner" className="flex items-center gap-3 mb-5 p-3 rounded-2xl bg-[#6C0BA9]/15 border border-[#6C0BA9]/40">
                <Avatar user={inviter} size={40} />
                <p className="text-sm"><span className="font-semibold">{inviter.name}</span> invited you to Xealth 🎉</p>
              </div>
            )}
            <div className="flex gap-1 p-1 rounded-2xl bg-secondary mb-6">
              {["login", "register"].map((m) => (
                <button
                  key={m}
                  data-testid={`auth-tab-${m}`}
                  onClick={() => setMode(m)}
                  className={`flex-1 py-2 rounded-xl text-sm font-semibold capitalize transition-all ${mode === m ? "bg-[#6C0BA9] text-white shadow-lg shadow-[#6C0BA9]/30" : "text-muted-foreground hover:text-foreground"}`}
                >
                  {m === "login" ? "Sign In" : "Sign Up"}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="space-y-3.5">
              {mode === "register" && (
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input data-testid="auth-name-input" required placeholder="Full name" className="pl-10 h-11 rounded-xl bg-input border-border" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
              )}
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input data-testid="auth-email-input" required type="email" placeholder="Email" className="pl-10 h-11 rounded-xl bg-input border-border" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input data-testid="auth-password-input" required type="password" placeholder="Password" className="pl-10 h-11 rounded-xl bg-input border-border" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              {mode === "register" && (
                <label className="flex items-start gap-2.5 text-xs text-muted-foreground cursor-pointer">
                  <Checkbox data-testid="auth-guidelines-checkbox" checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5 border-[#8012C6] data-[state=checked]:bg-[#6C0BA9]" />
                  <span>
                    I agree to the{" "}
                    <button type="button" data-testid="auth-guidelines-link" onClick={() => setShowRules(true)} className="text-[#C084FC] font-semibold hover:underline">Community Guidelines</button>
                    {" "}— be kind, no hate, no spam.
                  </span>
                </label>
              )}
              <Button data-testid="auth-submit-btn" disabled={busy} type="submit" className="w-full h-11 rounded-xl bg-[#6C0BA9] hover:bg-[#8012C6] text-white font-semibold shadow-lg shadow-[#6C0BA9]/30 transition-all">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "login" ? "Sign In" : "Create Account"}
              </Button>
            </form>

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground">or</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            <Button data-testid="google-login-btn" onClick={googleLogin} variant="outline" className="w-full h-11 rounded-xl border-border bg-secondary hover:bg-accent font-semibold gap-2">
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
              Continue with Google
            </Button>
          </div>
        </motion.div>
      </div>
      <Dialog open={showRules} onOpenChange={setShowRules}>
        <DialogContent data-testid="auth-guidelines-dialog" className="bg-card border-border rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Community Guidelines</DialogTitle></DialogHeader>
          <GuidelinesContent compact />
          <Button data-testid="auth-guidelines-agree-btn" onClick={() => { setAgreed(true); setShowRules(false); }} className="w-full rounded-xl bg-[#6C0BA9] hover:bg-[#8012C6] text-white">I agree</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
